import React, { useState } from 'react';
import PropTypes from 'prop-types';

import style from './style/dealingwithresistance.scss'
import gridStyle from '../Grid/style/grid.scss';
import icons from './../../style/icons.scss';

import ScoreBarTypes from '../../constants/ScoreBarTypes';
import ScoreRange from '../../constants/ScoreRange';

import AddOnIcon from '../AddOnIcon/AddOnIcon';
import AppendixScoreBarSection from '../AppendixScoreBarSection/AppendixScoreBarSection';
import FooterBar from '../SectionBlock/components/FooterBar/FooterBar';
import MultipleRankingSection from '../MultipleRankingSection/MultipleRankingSection';
import SectionBlock from '../SectionBlock/SectionBlock';

import { DealingWithResistanceDetailsModal } from './components/DealingWithResistanceDetailsModal';

/**
 * Renders the DealingWithResistance report section.
 * @param {Object} props - The component props.
 * @param {Function} props.getInitials - The function to get the participant's initials.
 * @param {String} props.handle - The handle of the report section.
 * @param {Object} props.i18nParticipation - The translations for the participation section.
 * @param {String} props.languageId - The users language ID.
 * @param {Object} props.reportData - The report data.
 * @param {Object} props.section - The section data.
 * @returns {JSX.Element} The DealingWithResistance report section.
 * @throws {Error} If section data is invalid.
 */

export const DealingWithResistance = (props) => {
    const {
        getInitials,
        handle,
        i18nParticipation,
        languageId,
        reportData,
        section
    } = props;

    const [scoreIndex, setScoreIndex] = useState(null);

    const initials = getInitials(reportData.participant);
    const riskIndicationData = {
        title: section.footerTitle,
        score: section.score,
        evaluation: section.evaluation,
        translations: i18nParticipation
    };


    return (
        <SectionBlock
            classes={ {
                root: gridStyle.scoreBlockWhite,
                badge: {
                    root: gridStyle.badge__sectionBlock,
                    figure: icons[`badgeFigure__${handle}`]
                }
            } }
        >
            <span className={ gridStyle.sectionBlock_subtitle }>{ section.basedOn || i18nParticipation.report_section_based_on_default_text }</span>
            <MultipleRankingSection
                classes={ {
                    root: style.multipleRankingSection__dealingWithResistance,
                    output: style.multipleRankingSection__output,
                    roundIcon: {
                        roundIconFigureWrapper: gridStyle.hidden
                    },
                    contentWrapper: gridStyle.sectionBlock__noPadding
                } }
                title={ section.title }
                iconText={ '' }
            >
                { section.introduction && <p className={ style.dealingWithResistance_introduction }>{ section.introduction }</p> }
                <div className={ style.dealingWithResistanceSection }>
                    { section.scores.map(
                        (score, index) => {
                            if (!score.ranges) {
                                return null;
                            }

                            return (
                                <div
                                    aria-label="Dealing with resistance score"
                                    className={ style.dealingWithResistanceSectionScoreBar }
                                    key={ `scale-${score.name}-${index}` }
                                    onClick={ () => setScoreIndex(index) }
                                    role='button'
                                >
                                    <AppendixScoreBarSection
                                        scoreValue={ score.score }
                                        scoreMin={ ScoreRange.DEALING_WITH_RESISTANCE.MIN }
                                        scoreMax={ ScoreRange.DEALING_WITH_RESISTANCE.MAX }
                                        lowLabel={ score.lowLabel }
                                        highLabel={ score.highLabel }
                                        scoreName={ score.name }
                                        scoreBarType={ ScoreBarTypes.GAUGE }
                                        initials={ initials }
                                        levelRanges={ score.ranges }
                                        classes={ {
                                            low: gridStyle.lowLabel,
                                            high: gridStyle.highLabel,
                                            scoreBlock: gridStyle.multipleRankingSectionScoreBar__scoreBlockResistance,
                                            colors: {
                                                red: style.red,
                                                orange: style.orange,
                                                green: style.green
                                            }
                                        } }
                                    />
                                    <div className={ style.dealingWithResistanceSectionIcon }>
                                        <AddOnIcon handle={ `icon_addon_${score.handle}` } />
                                        <p>{ score.name }</p>
                                    </div>
                                </div>
                            );
                        }
                    ) }
                    <DealingWithResistanceDetailsModal
                        changeScoreIndex={ setScoreIndex }
                        i18nParticipation= { i18nParticipation }
                        initials={ initials }
                        languageId={ languageId }
                        onClose={ () => setScoreIndex(null) }
                        scoreIndex={ scoreIndex }
                        section={ section }
                    />
                </div>
                <FooterBar data={ riskIndicationData } />
            </MultipleRankingSection>
        </SectionBlock>
    );
};

DealingWithResistance.propTypes = {
    getInitials: PropTypes.func.isRequired,
    handle: PropTypes.string.isRequired,
    i18nParticipation: PropTypes.object.isRequired,
    reportData: PropTypes.object.isRequired,
    section: PropTypes.object.isRequired,
    languageId: PropTypes.string
};
